import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  DAY_NAMES, secToLabel, addMinutesLabel, weekDates, occursOn, weeklyStats,
} from '../../lib/raceConflict';

/* 주간 캘린더 — **보기 전용**. 경기 일정만 확인한다.
   클릭 편집은 목록 뷰에서만 한다.
   상세: docs/race/제타트랙_레이스모드_작업계획서.md §6.3 */

const Wrap = styled.div``;

const Bar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; margin-bottom: 12px;
`;

const WeekNav = styled.div`display: flex; align-items: center; gap: 8px;`;

const NavBtn = styled.button`
  width: 30px; height: 30px; line-height: 1;
  border: 1px solid #e5e7eb; background: #fff; border-radius: 6px;
  color: #374151; cursor: pointer; font-size: 15px;
  &:hover { background: #f8f9fa; }
`;

const TodayBtn = styled.button`
  padding: 5px 11px; font-size: 13px; margin-left: 4px;
  border: 1px solid #e5e7eb; background: #fff; border-radius: 6px;
  color: #374151; cursor: pointer;
  &:hover:not(:disabled) { background: #f8f9fa; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const WeekLabel = styled.span`
  font-size: 14.5px; font-weight: 600; min-width: 160px; text-align: center;
  font-variant-numeric: tabular-nums;
`;

const Legend = styled.div`
  display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px; color: #6b7280;
`;

const Dot = styled.span`
  display: inline-block; width: 9px; height: 9px; border-radius: 50%;
  background: ${(p) => p.color}; margin-right: 6px;
`;

const Scroll = styled.div`overflow-x: auto;`;

/* table-layout: fixed 라 7개 요일 칸이 내용과 무관하게 같은 너비를 갖는다.
   auto 로 두면 경기가 있는 칸만 넓어져 표가 삐뚤어진다. */
const Table = styled.table`
  width: 100%; border-collapse: collapse; min-width: 760px; table-layout: fixed;

  th, td { border: 1px solid #f3f4f6; padding: 6px; vertical-align: top; font-size: 13px; text-align: center; }
  th { background: #f8f9fa; padding: 8px 4px; font-weight: 500; }
  th.today { background: #eff6ff; }
  td.hour { background: #f8f9fa; color: #6b7280; white-space: nowrap; font-variant-numeric: tabular-nums; padding-top: 10px; }
  td.today { background: #fbfdff; }
`;

const DayName = styled.div`
  font-size: 11.5px; letter-spacing: 0.3px; margin-bottom: 2px;
  color: ${(p) => p.today ? '#1d4ed8' : p.tone === 'sun' ? '#dc2626' : p.tone === 'sat' ? '#2563eb' : '#9ca3af'};
`;

const DayNum = styled.div`
  font-size: 15px; font-variant-numeric: tabular-nums;
  font-weight: ${(p) => p.today ? 700 : 500};
  color: ${(p) => p.today ? '#1d4ed8' : p.tone === 'sun' ? '#dc2626' : p.tone === 'sat' ? '#2563eb' : '#374151'};
`;

const Ev = styled.div`
  border-radius: 5px; padding: 6px 7px; font-size: 12px; line-height: 1.4; text-align: left;
  background: ${(p) => p.tone.bg}; color: ${(p) => p.tone.fg};
  overflow-wrap: anywhere;
`;

const EvTop = styled.div`font-weight: 600; display: flex; align-items: center; gap: 3px; flex-wrap: wrap;`;

const EvSub = styled.span`opacity: 0.85; font-variant-numeric: tabular-nums;`;

const Once = styled.span`
  display: inline-block; padding: 0 4px; border-radius: 3px;
  font-size: 10px; background: rgba(0, 0, 0, 0.12);
`;

const Stats = styled.div`
  display: flex; gap: 28px; margin-top: 18px; padding: 14px 18px;
  background: #f8f9fa; border-radius: 8px; flex-wrap: wrap;
`;

const Stat = styled.div``;
const StatKey = styled.div`font-size: 12px; color: #6b7280; margin-bottom: 4px;`;
const StatVal = styled.div`font-size: 18px; font-weight: 700; color: #111827;`;

const Empty = styled.div`padding: 40px 0; text-align: center; color: #9ca3af; font-size: 14px;`;

//--> 거리 3단계 색
const TONES = [
  { max: 1000, bg: '#e8f1fd', fg: '#1e4e8c', label: '1,000m 이하', dot: '#85b7eb' },
  { max: 2500, bg: '#e6f7f1', fg: '#0f6e56', label: '~2,500m', dot: '#5dcaa5' },
  { max: Infinity, bg: '#f3ecfb', fg: '#4b2c73', label: '2,500m 초과', dot: '#b79ada' },
];
const toneOf = (m) => TONES.find((t) => Number(m) <= t.max);

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const RaceScheduleCalendar = ({ schedules }) => {
  const [offset, setOffset] = useState(0);

  /* 표시 중인 주는 월요일 시작이다. 요일만 보면 1회성 경기가 매주 반복되는 것처럼
     보이므로, 칸마다 실제 날짜를 두고 그 날짜에 열리는지 확인한다. */
  const dates = useMemo(() => weekDates(offset), [offset]);
  const today = useMemo(() => new Date(), []);

  const on = (schedules || []).filter((r) => Number(r.active) !== 0);
  //--> 이 주에 한 번이라도 열리는 경기만 줄을 만든다
  const shown = on.filter((r) => dates.some((d) => occursOn(r, d)));
  const hours = [...new Set(shown.map((r) => Number(r.startTime)))].sort((a, b) => a - b);

  const stats = weeklyStats(schedules, dates);

  const first = dates[0];
  const last = dates[6];
  const weekLabel =
    `${first.getFullYear()}. ${first.getMonth() + 1}. ${first.getDate()}` +
    ` – ${last.getMonth() + 1}. ${last.getDate()}`;

  return (
    <Wrap>
      <Bar>
        <WeekNav>
          <NavBtn type="button" aria-label="이전 주" onClick={() => setOffset(offset - 1)}>‹</NavBtn>
          <WeekLabel>{weekLabel}</WeekLabel>
          <NavBtn type="button" aria-label="다음 주" onClick={() => setOffset(offset + 1)}>›</NavBtn>
          <TodayBtn type="button" disabled={offset === 0} onClick={() => setOffset(0)}>이번 주</TodayBtn>
        </WeekNav>
        <Legend>
          {TONES.map((t) => <span key={t.label}><Dot color={t.dot} />{t.label}</span>)}
        </Legend>
      </Bar>

      {hours.length === 0 ? (
        <Empty>이 주에 열리는 경기가 없습니다.</Empty>
      ) : (
        <Scroll>
          <Table>
            <colgroup>
              <col style={{ width: 62 }} />
            </colgroup>
            <thead>
              <tr>
                <th />
                {dates.map((d) => {
                  const isToday = isSameDay(d, today);
                  const tone = d.getDay() === 0 ? 'sun' : d.getDay() === 6 ? 'sat' : '';
                  return (
                    <th key={d.getTime()} className={isToday ? 'today' : undefined}>
                      <DayName today={isToday} tone={tone}>{DAY_NAMES[d.getDay()]}</DayName>
                      <DayNum today={isToday} tone={tone}>{d.getDate()}</DayNum>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {hours.map((sec) => (
                <tr key={sec}>
                  <td className="hour">{secToLabel(sec)}</td>
                  {dates.map((d) => {
                    const race = shown.find((r) => Number(r.startTime) === sec && occursOn(r, d));
                    const todayCls = isSameDay(d, today) ? 'today' : undefined;
                    if (!race) return <td key={d.getTime()} className={todayCls} />;
                    return (
                      <td key={d.getTime()} className={todayCls}>
                        <Ev tone={toneOf(race.distance)}>
                          <EvTop>
                            {Number(race.distance).toLocaleString()}m
                            {Number(race.repeatType) === 2 && <Once>1회</Once>}
                          </EvTop>
                          <EvSub>
                            ~{addMinutesLabel(race.startTime, race.endMin)} · {Number(race.prize1).toLocaleString()}P
                          </EvSub>
                        </Ev>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}

      <Stats>
        <Stat>
          <StatKey>{offset === 0 ? '이번 주 경기' : '이 주 경기'}</StatKey>
          <StatVal>{stats.count.toLocaleString()}회</StatVal>
        </Stat>
        <Stat>
          <StatKey>최대 지급</StatKey>
          <StatVal>{stats.point.toLocaleString()}P</StatVal>
        </Stat>
      </Stats>
    </Wrap>
  );
};

export default RaceScheduleCalendar;
